import { AlertCircle, CheckCircle2, CircleSlash, type LucideIcon } from 'lucide-react'
import type { StudioJob, StudioJobType } from '@/features/ai/studioTypes'
import { imageTargetLabel } from '@/features/ai/lib/imageTargetLabel'
import { blockTargetLabel } from '@/features/ai/lib/blockTargetLabel'
import { translationTargetLabel } from '@/features/ai/lib/translationTargetLabel'
import { lessonTargetLabel } from '@/features/ai/lib/lessonTargetLabel'
import { isFinalSkip, skipReasonText } from '@/features/ai/lib/skipReason'
import { messagesNow } from '@/i18n'
import { messages } from './sidebarMessages'

export interface Progress {
  done: number
  total: number | null
}

/**
 * Пять исходов, а не два. `failed` с НЕПУСТЫМ `result` — прерванный прогон:
 * часть работы записалась (поллер поэтому и инвалидирует кеш на `failed`), и
 * деньги за неё списаны. `success` с НУЛЁМ сделанного — тоже свой исход: у
 * картинок это штатный safety-блок модели, красить его в зелёную галочку
 * значит соврать, а в красный — контракт прямо запрещает (§5.4). `cancelled` —
 * пятый и последний: это решение автора, а не поломка и не успех, красить его
 * в чужой цвет значит соврать про то, кто остановил прогон.
 */
export type Outcome = 'not-started' | 'interrupted' | 'empty' | 'done' | 'cancelled'

/** Пояснение про промпт картинки: только у успешной правки блоков, что-то записавшей. */
export const showsImagePromptNote = (job: StudioJob, progress: Progress): boolean =>
  job.type === 'course_block_edit' && job.status === 'success' && progress.done > 0

export const outcomeOf = (job: StudioJob, progress: Progress): Outcome => {
  // Отмена — решение автора, а не поломка: ни красным, ни зелёной галочкой.
  if (job.status === 'cancelled') return 'cancelled'
  if (job.status === 'failed') {
    // `result === null` на терминальном `failed` — прогон не начинался вовсе
    // (n8n недоступен/отклонил), работы нет и денег не списано.
    return job.result === null ? 'not-started' : 'interrupted'
  }
  return progress.total !== null && progress.total > 0 && progress.done === 0 ? 'empty' : 'done'
}

/**
 * Глагол результата по типу прогона — рядом с `jobTypeLabel`, но отдельно:
 * там подпись СУЩЕСТВИТЕЛЬНЫМ («генерация картинок»), тут глагол исхода
 * («Нарисовано»/«Сделано»). Фолбэк на незнакомый тип — по образцу
 * `jobTypeLabel.ts`: молчаливая пустая строка в UI хуже разумного дефолта.
 */
const COUNTED: Record<StudioJobType, (counted: string) => string> = {
  course_generation: (c) => messagesNow(messages).countedGeneration(c),
  course_image_generation: (c) => messagesNow(messages).countedImages(c),
  course_block_edit: (c) => messagesNow(messages).countedBlockEdit(c),
  course_translation: (c) => messagesNow(messages).countedTranslation(c),
}

/** Берёт ТИП, а не джобу целиком: тот же глагол нужен закрытому итогу первой
 *  джобы цепочки (`PreviousRunNotice`), у которого на руках только снимок
 *  `previous`, а самой джобы уже нет. */
export const countedPhrase = (type: StudioJobType, progress: Progress): string =>
  (COUNTED[type] ?? COUNTED.course_generation)(
    messagesNow(messages).doneOfTotal(progress.done, progress.total ?? '—'),
  )

export interface SkipItem {
  key: string
  label: string
  reason: string
  /** Повтор упрётся в тот же отказ (`isFinalSkip`). Пропуск «место исчезло» кода не имеет — не final. */
  final: boolean
}

// §5.4: пустая строка в `result.images` — места больше нет (автор удалил блок,
// элемент или урок, пока шёл прогон) — подпись `messages.vanished`.

/**
 * Полный список пропусков прогона. У картинок он собирается ИЗ ДВУХ мест:
 * `result.skipped` (модель отказалась) и ключи `result.images` с пустым
 * значением — их `images_done` считает наравне с нарисованными, поэтому без
 * этой ветки итог говорит «Нарисовано 9 из 9», списывает за 7 и не объясняет
 * расхождение. Числитель серверный и здесь не трогается.
 *
 * Картиночные подписи (`imageTargetLabel`) применяются ТОЛЬКО к картиночному
 * прогону, подписи блоков (`blockTargetLabel`) — только к правке: у перевода
 * ключи другого контракта (§7.5), и «Картинка урока #50» там соврала бы про
 * суть пропуска. Незнакомому контракту — сырой ключ.
 *
 * Причина — по МАШИННОМУ коду (`result.skipped_codes`, PRD #60): известный код
 * даёт наш текст, незнакомый или отсутствующий — серверный дословно. Разбирать
 * сам текст сервера нельзя: угаданное сопоставление молча перестанет
 * работать при первой смене формулировки.
 */
/** Подпись цели — СВОЯ у каждого контракта: чужая соврала бы про суть
 *  пропуска. Карта ПОЛНАЯ (`Record`, не `Partial`) намеренно — появится
 *  пятый тип джобы, и сборка упадёт здесь, а не отдаст молча сырой ключ.
 *  Фолбэк ниже остаётся страховкой на рантайм: бэк вправе прислать тип,
 *  которого нет в объединении. */
const LABEL: Record<StudioJobType, (key: string) => string> = {
  course_generation: lessonTargetLabel,
  course_block_edit: blockTargetLabel,
  course_translation: translationTargetLabel,
  course_image_generation: imageTargetLabel,
}

/** Пропуски, объявленные сервером в `result.skipped`. Отдельно от
 *  `outcomeSkips`, потому что тот же список показывает закрытый итог первой
 *  джобы цепочки — у него на руках снимок `previous`, а не `StudioJob`. */
export const declaredSkips = (
  type: StudioJobType,
  skipped: Record<string, string> | undefined,
  codes?: Record<string, string>,
): SkipItem[] => {
  // Проверка СОБСТВЕННОГО ключа, а не `??`: у объектного литерала тип
  // `constructor` (бэк присылает тип строкой, без валидации) вернул бы
  // функцию мимо `??`, и в подпись уехал бы кусок кода. Ту же ловушку
  // `jobTypeLabel` обходит через `Map`; здесь хватает `hasOwnProperty` —
  // `Object.hasOwn` потребовал бы поднять `lib` проекта до ES2022.
  const known = Object.prototype.hasOwnProperty.call(LABEL, type)
  const label: (key: string) => string = known ? LABEL[type] : (key) => key
  const codeOf = (key: string) =>
    codes && Object.prototype.hasOwnProperty.call(codes, key) ? codes[key] : undefined
  return Object.entries(skipped ?? {}).map(([key, reason]) => ({
    key,
    label: label(key),
    reason: skipReasonText(codeOf(key), reason),
    final: isFinalSkip(codeOf(key)),
  }))
}

export const outcomeSkips = (job: StudioJob): SkipItem[] => {
  const declared = declaredSkips(job.type, job.result?.skipped, job.result?.skipped_codes)
  if (job.type !== 'course_image_generation') return declared
  const skipped = job.result?.skipped ?? {}
  const vanished = Object.entries(job.result?.images ?? {})
    // `key in skipped` — бэк вправе назвать причину сам; своя подпись поверх
    // его собственной была бы дублем строки об одном и том же месте.
    .filter(([key, url]) => url === '' && !(key in skipped))
    .map(([key]) => ({
      key,
      label: imageTargetLabel(key),
      reason: messagesNow(messages).vanished,
      final: false,
    }))
  return [...declared, ...vanished]
}

/** «Продолжить» бессмысленно, когда УСПЕШНЫЙ прогон пропустил только то, что
 *  повтор отобьёт так же. У прерванного плана продолжения несёт и
 *  необработанные блоки — там кнопку прятать нельзя. Считается по тому же
 *  списку, что рисует итог, — второй копии предиката нет. */
export const skipsAllFinal = (job: StudioJob, skips: SkipItem[]): boolean =>
  job.status === 'success' && skips.length > 0 && skips.every((s) => s.final)

/**
 * Иконка, тон заголовка и ПОВЕРХНОСТЬ одного исхода — в одной записи: итог был
 * единственной неокрашенной плашкой панели, и серый фон спорил с цветным
 * заголовком (R4). Манера та же, что у `CourseBusyNotice`: `/10` фон + `/30`
 * бордер. Радиус общий для всех плашек и по исходу не меняется.
 */
export const HEADER: Record<
  Outcome,
  { Icon: LucideIcon; tone: string; surface: string; title: (phrase: string) => string }
> = {
  'not-started': {
    Icon: AlertCircle,
    tone: 'text-destructive',
    surface: 'border-destructive/30 bg-destructive/10',
    title: () => messagesNow(messages).outcomeNotStarted,
  },
  interrupted: {
    Icon: AlertCircle,
    tone: 'text-warning',
    surface: 'border-warning/30 bg-warning/10',
    title: (phrase) => messagesNow(messages).outcomeInterrupted(phrase.toLowerCase()),
  },
  empty: {
    Icon: AlertCircle,
    tone: 'text-warning',
    surface: 'border-warning/30 bg-warning/10',
    title: (phrase) => phrase,
  },
  done: {
    Icon: CheckCircle2,
    tone: 'text-success',
    surface: 'border-success/30 bg-success/10',
    title: (phrase) => phrase,
  },
  cancelled: {
    Icon: CircleSlash,
    tone: 'text-muted-foreground',
    surface: 'border-border bg-muted/40',
    title: (phrase) => messagesNow(messages).outcomeCancelled(phrase.toLowerCase()),
  },
}
